import { Box, Typography, Paper, Stack, CircularProgress } from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq";
import { SsqAutocomplete, SsqOption } from "src/components/Selector/SsqSelector";
import { SettingsError } from "../SettingsError";
import { useApplicationParameters, useUpsertAppParam } from "src/api/client-api/appParams";
import { Suspense } from "src/components/utils/Suspense";
import { useEffect, useState } from "react";
import { useSnackbar } from "src/utils/useSnackbar";
import { isAxiosError } from "axios";
import { useQueryClient } from "react-query";
import { appParamCacheKey } from "src/api/client-api/appParams/useApplicationParameters";

export const DEFAULT_PACKAGING_SSQ_ID: number = 141;

export const PackagingsSsq = () => {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { applicationParameters } = useApplicationParameters();

    const [packagingsSsqId, setPackagingsSsqId] = useState<number | undefined>(undefined);
    const [selectedSsq, setSelectedSsq] = useState<SsqOption | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { ssq, isLoading: isSsqLoading, isError: isSsqError } = useGetOneSsq(packagingsSsqId);
    const { mutate: upsertAppParam } = useUpsertAppParam({ forceReload: false });

    const packagingSsqIdParam = applicationParameters?.["packagingsSsqId"];

    useEffect(() => {
        setPackagingsSsqId(Number(packagingSsqIdParam ?? DEFAULT_PACKAGING_SSQ_ID));
    }, [applicationParameters]);

    useEffect(() => {
        setSelectedSsq(ssq || null);
    }, [ssq]);

    if (isSsqError) {
        return (
            <SettingsError
                heading={t("settings.packagings.ssq", "Packaging's SSQ")}
                message={t(
                    "settings.packagings.error.loadFailed",
                    "Failed to load packaging's SSQ. Please try refreshing the page or contact support if the problem persists.",
                )}
            />
        );
    }

    return (
        <Box sx={{ padding: "32px" }}>
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box
                        sx={{
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                        onClick={() => navigate(-1)}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                    </Box>
                    <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                        {t("settings.packagings.ssq", "Packaging's SSQ")}
                    </Typography>
                </Box>

                {/* Content area */}
                <Stack sx={{ padding: "32px 24px" }} gap={2}>
                    <Typography variant="h5">{t("settings.packagings.ssq.title", "Choose packaging's SSQ")}</Typography>

                    <Suspense isLoading={isSsqLoading || isLoading} fallback={<CircularProgress />}>
                        <SsqAutocomplete
                            value={selectedSsq}
                            entityType="packaging"
                            onChange={(option) => {
                                if (!option) return;

                                setIsLoading(true);

                                upsertAppParam(
                                    {
                                        key: "packagingsSsqId",
                                        value: String(option.id),
                                        isNew: !packagingSsqIdParam,
                                    },
                                    {
                                        onSuccess: () => {
                                            setIsLoading(false);
                                            setSelectedSsq(option);

                                            queryClient.invalidateQueries([appParamCacheKey]);

                                            snackbar.setSnackbarProps({
                                                type: "success",
                                                message: t(
                                                    "settings.packagings.ssq.success",
                                                    "The packaging's SSQ has been updated successfuly!",
                                                ),
                                            });
                                        },
                                        onError: (error) => {
                                            setIsLoading(false);

                                            if (!isAxiosError(error)) {
                                                console.error(error);

                                                return;
                                            }

                                            snackbar.setSnackbarProps({
                                                type: "error",
                                                message: error?.response?.data?.["hydra:description"] ?? error?.message,
                                            });
                                        },
                                    },
                                );
                            }}
                        />
                    </Suspense>
                </Stack>
            </Paper>
        </Box>
    );
};
